/**
 * US counties — `GET /v2/counties/`.
 *
 * Backs the Schedule "County" dropdown (an atlas SearchSelect). The list
 * is filtered to the row's State (`?state=WI` → that state's counties),
 * and the SearchSelect provides the type-to-filter search over them. The
 * stored value is the county's FIPS code (id); the name is shown.
 */
import { getJsonReal } from "../client";

export interface County {
    fips: string;
    name: string;
    state: string | null;
}

interface CountyWire {
    fips: string;
    name: string;
    state: string | null;
}

interface CountiesListWire {
    total: number;
    items: CountyWire[];
}

function toCounty(c: CountyWire): County {
    return { fips: c.fips, name: c.name, state: c.state ?? null };
}

/** Page size — the backend caps `limit` at 200, and a few states (TX has
 *  254 counties) exceed it, so we page through with `offset`. */
const PAGE = 200;

/** Every county in a state (e.g. WI → 72, TX → 254). Pages through until
 *  the full set is collected. */
export async function listCountiesByState(state: string): Promise<County[]> {
    const fetchPage = (offset: number) => {
        const qs = new URLSearchParams({ state, limit: String(PAGE), offset: String(offset) });
        return getJsonReal<CountiesListWire>(`/v2/counties/?${qs.toString()}`);
    };
    const first = await fetchPage(0);
    const all = [...first.items];
    for (let offset = PAGE; offset < first.total; offset += PAGE) {
        const page = await fetchPage(offset);
        all.push(...page.items);
    }
    return all.map(toCounty);
}

/** Case/whitespace-insensitive name key; drops trailing punctuation and a
 *  trailing "County" / "Co." so the sheet's "Spartanburg County",
 *  "Spartanburg County." or "Spartanburg Co." meet the catalog's
 *  "Spartanburg". */
function nameKey(name: string): string {
    return name
        .trim()
        .toLowerCase()
        .replace(/[.,;\s]+$/, "")
        .replace(/\s+(county|co\.?)$/, "")
        .replace(/\s+/g, " ");
}

/** The county (FIPS) in `counties` that a stored value refers to — the
 *  FIPS itself (what the FE writes) or the county's name (what the import
 *  writes). `null` when nothing matches or the value is empty. */
export function resolveCountyKey(value: string, counties: County[]): string | null {
    const stored = value.trim();
    if (!stored) return null;
    const byFips = counties.find((c) => c.fips === stored);
    if (byFips) return byFips.fips;
    const key = nameKey(stored);
    return counties.find((c) => nameKey(c.name) === key)?.fips ?? null;
}
